"use client";

// Centered modal — the standard overlay for detail views and forms.
// (The side Drawer is reserved for mobile navigation.)
// Scroll lives on the inner body: the .glass pseudo layers (blur / refraction)
// are positioned against the outer box and must not scroll.

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Icon, { type IconName } from "./Icon";
import { useFocusTrap } from "../lib/useFocusTrap";

const SIZES = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-2xl",
} as const;

export default function Modal({
  open,
  onClose,
  title,
  subtitle,
  icon,
  size = "md",
  busy = false,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  icon?: IconName;
  size?: keyof typeof SIZES;
  /** Blocks backdrop / Escape dismissal while a request is in flight. */
  busy?: boolean;
  children: React.ReactNode;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[70] grid place-items-center p-4"
      onClick={() => !busy && onClose()}
    >
      <div className="overlay-backdrop absolute inset-0" />
      <div
        ref={dialogRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`glass glass-strong modal-pop relative w-full ${SIZES[size]} max-h-[88dvh] rounded-[26px] flex flex-col outline-none`}
      >
        {title && (
          <div className="flex items-center gap-3 px-6 sm:px-7 pt-6 sm:pt-7 pb-4 shrink-0">
            {icon && (
              <span
                className="chip grid place-items-center w-11 h-11 rounded-2xl shrink-0"
                style={{ color: "var(--accent)", "--tint-color": "var(--mint)" } as React.CSSProperties}
              >
                <Icon name={icon} size={20} />
              </span>
            )}
            <div className="min-w-0">
              <h2 className="font-display text-xl text-text truncate">{title}</h2>
              {subtitle && <p className="text-[12.5px] text-text-faint truncate">{subtitle}</p>}
            </div>
            <button
              onClick={() => !busy && onClose()}
              aria-label="Close"
              className="neu-btn ml-auto grid place-items-center w-9 h-9 rounded-xl text-text-muted text-lg leading-none shrink-0"
            >
              ✕
            </button>
          </div>
        )}

        <div className={`flex-1 overflow-y-auto px-6 sm:px-7 pb-6 sm:pb-7 ${title ? "" : "pt-6 sm:pt-7"}`}>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
